import React, {useCallback, useEffect, useMemo, useRef, useState} from "react";
import {MonitorHeartOutlined, MonitorHeartRounded, RefreshOutlined} from "@mui/icons-material";
import {Badge, Box, Button, Card, CircularProgress, Container, IconButton, Popover, Typography, useMediaQuery} from "@mui/material";
import debounce from "lodash/debounce";
import useIsMounted from "../../../../hooks/use-is-mounted";
import useAccessLevels from "../../../../hooks/use-access-levels";
import Api from "../../../../../core/services/api/api";
import useIndustry from "../../../../hooks/use-industry";
import Grid2 from "@mui/material/Unstable_Grid2";
import DateUtils from "../../../../../core/services/date-utils";
import {useTranslation} from "react-i18next";

const useSystemHealthEndAction = (breakpoint) => {
    const [popoverAnchor, setPopoverAnchor] = useState();
    const [lastReport, setLastReport] = useState();
    const passedBreakpoint = useMediaQuery((theme) => theme.breakpoints.up(breakpoint));
    const popoverAction = useRef(null);
    const operatorAccessLevels = useAccessLevels();
    const {industries} = useIndustry({noCategories: true});
    const {t} = useTranslation("components", {keyPrefix: 'panel-sections.app-bar.actions.system-health'});

    const accessToNoHealthCheck = useMemo(() => {
        return !(
            operatorAccessLevels.packages.base.customerList
            || operatorAccessLevels.packages.sales.pricesInLookup
            || operatorAccessLevels.packages.lookup.nexusSearch
            || operatorAccessLevels.packages.base.operatorsList
            || operatorAccessLevels.packages.sales.quotesList
            || operatorAccessLevels.packages.sales.invoicesList
            || operatorAccessLevels.packages.sales.salesOrdersList
            || operatorAccessLevels.packages.sales.accountReceivables
        )
    }, [operatorAccessLevels.packages.base.customerList, operatorAccessLevels.packages.base.operatorsList, operatorAccessLevels.packages.lookup.nexusSearch, operatorAccessLevels.packages.sales.accountReceivables, operatorAccessLevels.packages.sales.invoicesList, operatorAccessLevels.packages.sales.pricesInLookup, operatorAccessLevels.packages.sales.quotesList, operatorAccessLevels.packages.sales.salesOrdersList])

    /**
     * With each change in the [passedBreakpoint] value:
     * - Close the popover
     */
    useEffect(() => {
        setPopoverAnchor(undefined)
    }, [passedBreakpoint])

    /**
     * Update the popover position on window resize.
     */
    useEffect(() => {
        if (!popoverAnchor)
            return;
        const debounced = debounce(() => {
            popoverAction.current?.updatePosition()
        }, 1000)
        window.addEventListener('resize', debounced);
        return () => window.removeEventListener('resize', debounced);
    }, [popoverAnchor])

    /**
     * Open the plugins popover
     */
    const openPopover = useCallback((e, anchorElement) => {
        setPopoverAnchor(anchorElement ?? e.currentTarget);
    }, [])

    /**
     * Close the plugins popover
     */
    const closePopover = useCallback(() => {
        setPopoverAnchor(undefined)
    }, [])


    const popover = useMemo(() => (
        <Popover
            id={'system-health-popover'}
            action={popoverAction}
            anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'center',
            }}
            transformOrigin={{
                vertical: 'top',
                horizontal: 'right',
            }}
            slotProps={{root: {sx: {marginBlockStart: 2}}, paper: {sx: (theme) => ({boxShadow: theme.shadows[2]})}}}
            open={!!popoverAnchor}
            anchorEl={popoverAnchor}
            onClose={closePopover}
        >
            <SystemHealthPopover
                lastReport={lastReport}
                setLastReport={setLastReport}
                industries={industries}
            />
        </Popover>
    ), [popoverAnchor, closePopover, lastReport, industries]);

    return useMemo(() =>
            accessToNoHealthCheck
                ? null
                : ({
                    key: "sys-health",
                    title: t('title'),
                    icon:
                        <Badge
                            badgeContent={""}
                            invisible={!lastReport}
                            slotProps={{
                                badge: {
                                    sx: {minWidth: 10, height: 10, width: 10, p: 0, bottom: 0, top: 'initial', transform: 'scale(1) translate(50%, 50%)'},
                                },
                            }}
                            color={
                                lastReport?.internal?.overallStatus === 'error' || lastReport?.external?.overallStatus === 'error'
                                    ? 'error'
                                    : lastReport?.internal?.overallStatus === 'slow' || lastReport?.external?.overallStatus === 'slow'
                                        ? 'warning'
                                        : lastReport?.internal?.overallStatus === 'skipped' || lastReport?.external?.overallStatus === 'skipped'
                                            ? 'default'
                                            : lastReport?.internal?.overallStatus === 'ok' && lastReport?.external?.overallStatus === 'ok'
                                                ? 'success'
                                                : 'default'
                            }
                        >
                            {
                                popoverAnchor
                                    ? <MonitorHeartRounded/>
                                    : <MonitorHeartOutlined/>
                            }
                        </Badge>,
                    color: "primary",
                    onClick: openPopover,
                    children: popover,
                }),
        [accessToNoHealthCheck, lastReport, popoverAnchor, openPopover, popover, t])
}

const SystemHealthPopover = ({lastReport, setLastReport, industries}) => {
    const hasLastReport = useMemo(() => !!lastReport, [lastReport]);
    const [loadingInternalHealth, setLoadingInternalHealth] = useState(!hasLastReport);
    const [loadingExternalHealth, setLoadingExternalHealth] = useState(!hasLastReport);
    const operatorAccessLevels = useAccessLevels();
    const [report, _setReport] = useState(lastReport);
    const isMounted = useIsMounted();
    const {t} = useTranslation("components", {keyPrefix: 'panel-sections.app-bar.actions.system-health'});

    const defaultIndustryId = useMemo(() => (industries?.find(e => e.isDefault) ?? industries?.[0])?.id, [industries]);

    const checkingSystemHealth = useMemo(() => loadingInternalHealth || loadingExternalHealth, [loadingInternalHealth, loadingExternalHealth])

    /**
     * Sets the Report and the Last Report state
     * @type {(function(*): void)}
     */
    const setReport = useCallback((newReportOrFunc) => {
        _setReport((prevState) => {
            const newReport = typeof newReportOrFunc === 'function' ? newReportOrFunc(prevState) : newReportOrFunc;
            setLastReport(newReport);
            return newReport;
        });
    }, [setLastReport])

    /**
     * Creates a report object from the given report data
     * @type {function(*): {avgTime: *, overallStatus: string, details: unknown[], lastCheck: string}}
     */
    const createReport = useCallback((report) => {
        const resultsVal = Object.values(report);
        const avgTime = resultsVal.reduce((acc, curr) => acc + curr.time, 0) / resultsVal.filter(r => r.status !== 'skipped').length;
        return {
            avgTime: avgTime,
            overallStatus: resultsVal.some(r => r.status === 'error')
                ? 'error'
                : resultsVal.every(r => r.status === 'skipped')
                    ? 'skipped' : avgTime > 5000
                        ? 'slow'
                        : 'ok',
            details: resultsVal,
            lastCheck: DateUtils.localNow().format(DateUtils.withoutOffsetFormat),
        }
    }, [])

    const createUnAuthorizedOrAccessDeniedCallback = useCallback((callback) => (response) => {
        switch (response?.errorCode ?? response?.code) {
            case 401:
            case -401:
            case 403:
            case -403:
                callback();
                break;
            default:
                break;
        }
    }, [])

    /**
     * Checks the Internal System Health
     * @type {(function(): Promise<*>)}
     */
    const checkInternalSystemHealth = useCallback(async () => {
        setLoadingInternalHealth(true);
        const results = {
            customers: null,
            pricelevels: null,
            products: null,
            operators: null,
        };
        for (let key in results) {
            if (!isMounted())
                return;
            switch (key) {
                case 'customers':
                    if (!operatorAccessLevels.packages.base.customerList) {
                        results[key] = {status: 'skipped', time: 0};
                        continue;
                    } else {
                        const time = Date.now();
                        const res = await Api.searchCustomersV2(
                            {sorts: [], pagination: {pageSize: 5, currentPage: 1}, keyword: null},
                            undefined,
                            createUnAuthorizedOrAccessDeniedCallback(() => {
                                results[key] = {status: 'skipped', time: 0};
                            })
                        );
                        if (results[key]?.status !== 'skipped')
                            results[key] = {status: res?.resultFlag ? 'ok' : 'error', time: Date.now() - time};
                    }
                    break;
                case 'pricelevels':
                    if (!operatorAccessLevels.packages.sales.pricesInLookup) {
                        results[key] = {status: 'skipped', time: 0};
                        continue;
                    } else {
                        const time = Date.now();
                        const res = await Api.getAllPriceLevels(
                            createUnAuthorizedOrAccessDeniedCallback(() => {
                                results[key] = {status: 'skipped', time: 0};
                            })
                        )
                        if (results[key]?.status !== 'skipped')
                            results[key] = {status: res?.resultFlag ? 'ok' : 'error', time: Date.now() - time};
                    }
                    break;
                case 'products':
                    if (!operatorAccessLevels.packages.lookup.nexusSearch || !defaultIndustryId) {
                        results[key] = {status: 'skipped', time: 0};
                        continue;
                    } else {
                        const time = Date.now();
                        const res = await Api.nexusSearch(
                            {isAdvancedMode: false, industryId: defaultIndustryId, keyword: '', pagination: {pageSize: 5, currentPage: 1}},
                            undefined,
                            createUnAuthorizedOrAccessDeniedCallback(() => {
                                results[key] = {status: 'skipped', time: 0};
                            })
                        )
                        if (results[key]?.status !== 'skipped')
                            results[key] = {status: res?.resultFlag ? 'ok' : 'error', time: Date.now() - time};
                    }
                    break;
                case 'operators':
                    if (!operatorAccessLevels.packages.base.operatorsList) {
                        results[key] = {status: 'skipped', time: 0};
                        continue;
                    } else {
                        const time = Date.now();
                        const res = await Api.searchOperatorsLocal(
                            {paginationInfo: {pageSize: 5, currentPage: 1}, filters: {}},
                            undefined,
                            createUnAuthorizedOrAccessDeniedCallback(() => {
                                results[key] = {status: 'skipped', time: 0};
                            })
                        )
                        if (results[key]?.status !== 'skipped')
                            results[key] = {status: res?.resultFlag ? 'ok' : 'error', time: Date.now() - time};
                    }
                    break;
                default:
                    results[key] = null;
                    break;
            }
        }
        if (!isMounted())
            return;
        setReport((prev) => ({...prev, internal: createReport(results)}));
        setLoadingInternalHealth(false);
    }, [createReport, createUnAuthorizedOrAccessDeniedCallback, defaultIndustryId, isMounted, operatorAccessLevels.packages.base.customerList, operatorAccessLevels.packages.base.operatorsList, operatorAccessLevels.packages.lookup.nexusSearch, operatorAccessLevels.packages.sales.pricesInLookup, setReport])

    /**
     * Checks the External System Health
     * @type {(function(): Promise<*>)}
     */
    const checkExternalSystemHealth = useCallback(async () => {
        setLoadingExternalHealth(true);
        const results = {
            quotes: null,
            invoices: null,
            salesOrders: null,
            accountReceivables: null,
        }
        for (let key in results) {
            if (!isMounted())
                return;
            switch (key) {
                case 'quotes':
                    if (!operatorAccessLevels.packages.sales.quotesList) {
                        results[key] = {status: 'skipped', time: 0};
                        continue;
                    } else {
                        const time = Date.now();
                        const res = await Api.searchQuotes(
                            {filters: {}, paginationInfo: {pageSize: 5, currentPage: 1}},
                            undefined,
                            createUnAuthorizedOrAccessDeniedCallback(() => {
                                results[key] = {status: 'skipped', time: 0};
                            })
                        );
                        if (results[key]?.status !== 'skipped')
                            results[key] = {status: res?.resultFlag ? 'ok' : 'error', time: Date.now() - time};
                    }
                    break;
                case 'invoices':
                    if (!operatorAccessLevels.packages.sales.invoicesList) {
                        results[key] = {status: 'skipped', time: 0};
                        continue;
                    } else {
                        const time = Date.now();
                        const res = await Api.searchInvoices(
                            {filters: {}, paginationInfo: {pageSize: 5, currentPage: 1}},
                            undefined,
                            createUnAuthorizedOrAccessDeniedCallback(() => {
                                results[key] = {status: 'skipped', time: 0};
                            })
                        );
                        if (results[key]?.status !== 'skipped')
                            results[key] = {status: res?.resultFlag ? 'ok' : 'error', time: Date.now() - time};
                    }
                    break;
                case 'salesOrders':
                    if (!operatorAccessLevels.packages.sales.salesOrdersList) {
                        results[key] = {status: 'skipped', time: 0};
                        continue;
                    } else {
                        const time = Date.now();
                        const res = await Api.searchSalesOrders(
                            {filters: {}, pagination: {pageSize: 5, currentPage: 1}},
                            undefined,
                            createUnAuthorizedOrAccessDeniedCallback(() => {
                                results[key] = {status: 'skipped', time: 0};
                            })
                        );
                        if (results[key]?.status !== 'skipped')
                            results[key] = {status: res?.resultFlag ? 'ok' : 'error', time: Date.now() - time};
                    }
                    break;
                case 'accountReceivables':
                    if (!operatorAccessLevels.packages.sales.accountReceivables) {
                        results[key] = {status: 'skipped', time: 0};
                        continue;
                    } else {
                        const time = Date.now();
                        const res = await Api.getARAgedList(
                            {keyword: '', pagination: {pageSize: 5, currentPage: 1}},
                            undefined,
                            createUnAuthorizedOrAccessDeniedCallback(() => {
                                results[key] = {status: 'skipped', time: 0};
                            })
                        );
                        if (results[key]?.status !== 'skipped')
                            results[key] = {status: res?.resultFlag ? 'ok' : 'error', time: Date.now() - time};
                    }
                    break;
                default:
                    results[key] = null;
                    break;
            }
        }
        if (!isMounted())
            return;
        setReport((prev) => ({...prev, external: createReport(results)}));
        setLoadingExternalHealth(false);
    }, [createReport, createUnAuthorizedOrAccessDeniedCallback, isMounted, operatorAccessLevels.packages.sales.accountReceivables, operatorAccessLevels.packages.sales.invoicesList, operatorAccessLevels.packages.sales.quotesList, operatorAccessLevels.packages.sales.salesOrdersList, setReport])

    /**
     * Checks the system Health
     * @type {(function(): Promise<*>)}
     */
    const checkSystemHealth = useCallback(async (type) => {
        switch (type) {
            case 'internal': {
                await checkInternalSystemHealth()
                break;
            }
            case 'external': {
                await checkExternalSystemHealth()
                break;
            }
            default: {
                await Promise.all([
                    checkInternalSystemHealth(),
                    checkExternalSystemHealth()
                ])
                break;
            }
        }
    }, [checkExternalSystemHealth, checkInternalSystemHealth])

    /**
     * On component mount, if there's no last report, check the system health
     */
    useEffect(() => {
        if (!hasLastReport) {
            void checkSystemHealth();
        }
    }, [hasLastReport, checkSystemHealth]);

    const healthReportSummary = useMemo(() => {
        return [
            {
                label: t('internalServices'),
                loading: loadingInternalHealth,
                avgTimeInSeconds: ((report?.internal?.avgTime ?? 0) / 1000).toFixed(2),
                refreshAction: () => checkSystemHealth('internal'),
                lastCheck: report?.internal?.lastCheck,
                statusColor: report?.internal?.overallStatus === 'ok'
                    ? 'success.main'
                    : report?.internal?.overallStatus === 'slow'
                        ? 'warning.main'
                        : report?.internal?.overallStatus === 'error'
                            ? 'error.main'
                            : report?.internal?.overallStatus === 'skipped'
                                ? 'text.secondary'
                                : 'text.disabled',
                statusText: report?.internal?.overallStatus === 'ok'
                    ? t('statusOk')
                    : report?.internal?.overallStatus === 'slow'
                        ? t('statusSlow')
                        : report?.internal?.overallStatus === 'error'
                            ? t('statusError')
                            : report?.internal?.overallStatus === 'skipped'
                                ? t('statusSkipped')
                                : t('statusOther'),
            },
            {
                label: t('externalServices'),
                loading: loadingExternalHealth,
                avgTimeInSeconds: ((report?.external?.avgTime ?? 0) / 1000).toFixed(2),
                refreshAction: () => checkSystemHealth('external'),
                lastCheck: report?.external?.lastCheck,
                statusColor: report?.external?.overallStatus === 'ok'
                    ? 'success.main'
                    : report?.external?.overallStatus === 'slow'
                        ? 'warning.main'
                        : report?.external?.overallStatus === 'error'
                            ? 'error.main'
                            : report?.external?.overallStatus === 'skipped'
                                ? 'text.secondary'
                                : 'text.disabled',
                statusText: report?.external?.overallStatus === 'ok'
                    ? t('statusOk')
                    : report?.external?.overallStatus === 'slow'
                        ? t('statusPerformanceIssue')
                        : report?.external?.overallStatus === 'error'
                            ? t('statusError')
                            : report?.external?.overallStatus === 'skipped'
                                ? t('statusSkipped')
                                : t('statusOther'),
            },
        ]
    }, [t, loadingInternalHealth, report?.internal?.avgTime, report?.internal?.lastCheck, report?.internal?.overallStatus, report?.external?.avgTime, report?.external?.lastCheck, report?.external?.overallStatus, loadingExternalHealth, checkSystemHealth])

    return (
        <>
            <Container maxWidth={'xs'} sx={{display: 'flex', flexDirection: 'column', gap: 2, minHeight: '310px'}} disableGutters>
                <Box sx={{display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: 2}}>
                    <Box sx={{display: 'flex', flexDirection: 'column'}}>
                        <Typography variant={'body1'} fontWeight={600}>
                            {t('title')}
                        </Typography>
                        <Typography variant={'caption'} color={'text.secondary'}>
                            {
                                report?.internal?.lastCheck || report?.external?.lastCheck
                                    ? t('lastChecked', {
                                        date: DateUtils.local(
                                            report?.internal?.lastCheck && report?.external?.lastCheck
                                                ? DateUtils.local(report?.internal?.lastCheck).isAfter(DateUtils.local(report?.external?.lastCheck))
                                                    ? report?.internal?.lastCheck
                                                    : report?.external?.lastCheck
                                                : report?.internal?.lastCheck || report?.external?.lastCheck
                                        ).fromNow()
                                    })
                                    : t('notCheckedYet')
                            }
                        </Typography>
                    </Box>
                    <Button
                        onClick={() => checkSystemHealth()}
                        size={'small'}
                        color={'primary'}
                        variant={'outlined'}
                        disabled={checkingSystemHealth}
                        startIcon={checkingSystemHealth ? <CircularProgress size={24} color={'inherit'}/> : <RefreshOutlined/>}
                    >
                        {checkingSystemHealth ? t('checking') : t('check')}
                    </Button>
                </Box>
                <Grid2 container spacing={1} sx={{paddingInline: 1.5}}>
                    {
                        healthReportSummary.map(item => (
                            <Grid2 key={item.label} xs={12}>
                                <Card variant={'outlined'} sx={{padding: 1.5, display: 'flex', gap: 1, justifyContent: 'space-between', alignItems: 'center'}}>
                                    <Box sx={{display: 'flex', flexDirection: 'column'}}>
                                        <Typography variant={'body2'} fontWeight={500} gutterBottom>
                                            {item.label}
                                        </Typography>
                                        <Box sx={{display: 'flex', alignItems: 'center', gap: 0.5}}>
                                            <Typography variant={'caption'} color={'text.primary'}>
                                                <Typography component={'span'} variant={'caption'} color={'text.secondary'} fontWeight={600}>
                                                    {t('lastCheckedItem')}
                                                </Typography> {item.loading ? t('checking') : item.lastCheck ? DateUtils.local(item.lastCheck).fromNow() : ""}
                                            </Typography>
                                            <IconButton
                                                size={'small'}
                                                onClick={item.refreshAction}
                                                disabled={item.loading}
                                                color={'paper.secondary'}
                                            >
                                                {
                                                    item.loading
                                                        ? <CircularProgress size={16}/>
                                                        : <RefreshOutlined fontSize={'small'}/>
                                                }
                                            </IconButton>
                                        </Box>
                                        {
                                            !item.loading &&
                                            <Typography variant={'caption'} color={'text.primary'}>
                                                <Typography component={'span'} variant={'caption'} color={'text.secondary'} fontWeight={600}>{t('avgResponseTime')}</Typography> {item.avgTimeInSeconds}s
                                            </Typography>
                                        }
                                    </Box>
                                    <Box sx={{display: 'flex', alignItems: 'center', gap: 0.5, justifyContent: 'flex-end'}}>
                                        <Card sx={{padding: 1, display: 'flex', alignItems: 'center', gap: 0.5, justifyContent: 'center', backgroundColor: 'background.default'}}>
                                            {
                                                item.loading
                                                    ? <CircularProgress size={20}/>
                                                    : <Box
                                                        sx={{
                                                            width: 10,
                                                            height: 10,
                                                            borderRadius: '50%',
                                                            backgroundColor: item.statusColor,
                                                        }}
                                                    />
                                            }
                                            <Typography variant={'body2'} fontWeight={500}>
                                                {
                                                    item.loading
                                                        ? t('checking')
                                                        : item.statusText
                                                }
                                            </Typography>
                                        </Card>
                                    </Box>
                                </Card>
                            </Grid2>
                        ))
                    }
                </Grid2>
            </Container>
        </>
    );
}

export default useSystemHealthEndAction;
